import { useState } from "react";
import PageHero from "@/components/PageHero";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We received your message.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero eyebrow="Get in touch" title="CONTACT US" subtitle="Talk to the JPA team. We reply within 48 hours." image="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=1800" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <h2 className="font-display text-4xl tracking-wider">Reach us directly</h2>
          <div className="mt-8 space-y-4">
            <a href="tel:+919304757995" className="flex items-center gap-3 border border-jpa-border p-4 hover:border-jpa-primary transition-colors"><Phone className="w-5 h-5 text-jpa-primary" /> +91 93047 57995</a>
            <a href="mailto:info@jpaofficial.com" className="flex items-center gap-3 border border-jpa-border p-4 hover:border-jpa-primary transition-colors"><Mail className="w-5 h-5 text-jpa-primary" /> info@jpaofficial.com</a>
            <a href="https://wa.me/919304757995" target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-jpa-border p-4 hover:border-jpa-primary transition-colors"><MessageCircle className="w-5 h-5 text-jpa-primary" /> WhatsApp: +91 93047 57995</a>
            <div className="flex items-start gap-3 border border-jpa-border p-4"><MapPin className="w-5 h-5 text-jpa-primary mt-1" /> 3-S Geeta Colony, Latma Road, Singh More, Ranchi, Jharkhand 834003</div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 border border-jpa-border p-8 bg-jpa-surface rounded-sm">
          <h2 className="font-display text-4xl tracking-wider">Send us a message</h2>
          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div><Label>Name</Label><Input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Email</Label><Input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div><Label>Phone (10 digits)</Label><Input inputMode="numeric" maxLength={10} pattern="\d{10}" placeholder="9876543210" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-1 rounded-sm" /></div>
            <div><Label>Subject</Label><Input required data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 rounded-sm" /></div>
          </div>
          <div className="mt-4"><Label>Message</Label><Textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 rounded-sm" /></div>
          <Button disabled={loading} type="submit" data-testid="contact-submit" className="mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm px-8 h-12">
            {loading ? "Sending..." : "Send message"}
          </Button>
        </form>
      </div>
    </div>
  );
}
